import { useEffect, useState, useCallback } from 'react';
import { fetchSingleRestaurant } from '../api/hotelinfoApi';
import { fetchRestaurantOrders } from '../api/orderApi';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Tabs, Tab, Box, Typography, Breadcrumbs, Link } from '@mui/material';
import RestaurantProfile from '../components/RestaurantManager/RestaurantProfile';
import RestaurantMenu from '../components/RestaurantManager/RestaurantMenu';
import RestaurantPromotion from '../components/RestaurantManager/RestaurantPromotion';
import RestaurantBooking from '../components/RestaurantManager/RestaurantBooking';
import RestaurantOrders from '../components/RestaurantManager/RestaurantOrders';

export default function RestaurantManager({ propertyId }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [tabIndex, setTabIndex] = useState(() => {
    const param = parseInt(searchParams.get('fromTab'), 10);
    return Number.isNaN(param) ? 0 : param;
  });
  const [restaurantName, setRestaurantName] = useState('');
  const [restaurantData, setRestaurantData] = useState(null);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadOrders = useCallback(async () => {
    if (!restaurantData?._id) return;
    setOrdersLoading(true);
    setOrdersError(null);
    try {
      const { data } = await fetchRestaurantOrders({ restaurantId: restaurantData._id });
      const list = Array.isArray(data) ? data : Array.isArray(data?.orders) ? data.orders : [];
      setOrders(list);
    } catch (err) {
      setOrdersError(err?.response?.data?.message || err?.message || 'Failed to load orders');
    } finally {
      setOrdersLoading(false);
    }
  }, [restaurantData?._id]);

  useEffect(() => {
    if (tabIndex === 2) {
      loadOrders();
    }
  }, [loadOrders, tabIndex]);

  useEffect(() => {
    if (!propertyId || !id) return;

    const fetchRestaurant = async () => {
      setLoading(true);
      try {
        const data = await fetchSingleRestaurant(id, propertyId);
        if (data?.name) setRestaurantName(data.name);
        setRestaurantData(data);
      } catch (err) {
        console.error('Failed to fetch restaurant data:', err);
        // Optionally, handle the error state here
      } finally {
        setLoading(false);
      }
    };

    fetchRestaurant();
  }, [id, propertyId]);

  useEffect(() => {
    const param = parseInt(searchParams.get('fromTab'), 10);
    if (!Number.isNaN(param) && param !== tabIndex) {
      setTabIndex(param);
    }
  }, [searchParams, tabIndex]);

  const handleTabChange = (_event, val) => {
    setTabIndex(val);
    const params = new URLSearchParams(searchParams);
    params.set('fromTab', val);
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
  };

  const handleUpdateOrder = useCallback((updatedOrder) => {
    setOrders((prevOrders) =>
      prevOrders.map((order) =>
        order._id === updatedOrder._id ? updatedOrder : order
      )
    );
  }, []);

  const resolvedPropertyId = propertyId || restaurantData?.propertyId;

  return (
    <Box sx={{ pt: 0, px: 3 }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
        <Link
          underline="hover"
          color="inherit"
          onClick={() => {
            const backTab = searchParams.get('fromTab');
            navigate(`/hotel-info-v2${backTab ? `?tab=${backTab}` : ''}`);
          }}
          sx={{ cursor: 'pointer' }}
        >
          Back
        </Link>
        <Typography color="text.primary">{restaurantName || 'Restaurant Manager'}</Typography>
      </Breadcrumbs>


      <Tabs value={tabIndex} onChange={handleTabChange} aria-label="Restaurant Subtabs">
        <Tab label="Profile" />
        <Tab label="Menu" />
        <Tab label="Orders" />
        <Tab label="Booking" />
        <Tab label="Promotion" />
      </Tabs>

      <Box sx={{ mt: 0 }}>
        {tabIndex === 0 && <RestaurantProfile restaurantData={restaurantData} loading={loading} />}
        {tabIndex === 1 && (
          <RestaurantMenu
            menu={restaurantData?.menu || []}
            propertyId={resolvedPropertyId}
            restaurantId={restaurantData?._id}
            loading={loading}
            setMenu={(updater) => {
              setRestaurantData(prev => ({
                ...prev,
                menu: typeof updater === 'function' ? updater(prev?.menu || []) : updater
              }));
            }}
            onImageUpdate={(itemId, newImageUrl) => {
              setRestaurantData(prev => ({
                ...prev,
                menu: (prev?.menu || []).map(item =>
                  item._id === itemId ? { ...item, image: newImageUrl } : item
                )
              }));
            }}
          />
        )}
        {tabIndex === 2 && restaurantData && (
          <RestaurantOrders
            restaurantId={restaurantData._id}
            propertyId={resolvedPropertyId}
            orders={orders}
            loading={ordersLoading}
            error={ordersError}
            onRefresh={loadOrders}
            onUpdateOrder={handleUpdateOrder}
          />
        )}
        {tabIndex === 3 && restaurantData && (
          <RestaurantBooking restaurantId={restaurantData._id} />
        )}
        {tabIndex === 4 && restaurantData && (() => {
          console.log('📦 Promotion Debug → restaurantId:', restaurantData._id);
          console.log('📦 Promotion Debug → resolvedPropertyId:', resolvedPropertyId);
          return (
            <RestaurantPromotion
              restaurantId={restaurantData._id}
              propertyId={resolvedPropertyId}
              menuItems={restaurantData.menu}
              loading={loading}
              setMenuItems={(menu) => {
                setRestaurantData(prev => ({
                  ...prev,
                  menu: [...menu]
                }));
              }}
              onMenuItemsUpdate={(menu) => {
                setRestaurantData(prev => ({
                  ...prev,
                  menu: [...menu]
                }));
              }}
            />
          );
        })()}
      </Box>
    </Box>
  );
}
